import { useEffect, useMemo, useState } from 'react';
import {
  ALL_MODULES,
  Permission,
  PermissionEffect,
  PermissionSubjectType,
  PortalModule,
  Role,
  isAdmin,
  type ModuleGrantDto,
  type PermissionGrantDto,
  type PersonSummaryDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { MODULE_KEY } from '../portal/modules';
import { useT } from '../lib/i18n';

const SUBJECT_LABEL_KEYS: Record<PermissionSubjectType, string> = {
  [PermissionSubjectType.Role]: 'admin.permissions.subject.role',
  [PermissionSubjectType.User]: 'admin.permissions.subject.user',
  [PermissionSubjectType.Group]: 'admin.permissions.subject.group',
};
const PERMISSION_LABEL_KEYS: Record<Permission, string> = {
  [Permission.ViewReserved]: 'admin.permissions.permission.viewReserved',
};
const ROLE_LABEL_KEYS: Record<Role, string> = {
  [Role.Onboarding]: 'admin.permissions.role.onboarding',
  [Role.Hr]: 'admin.permissions.role.hr',
  [Role.Researcher]: 'admin.permissions.role.researcher',
  [Role.Direction]: 'admin.permissions.role.direction',
  [Role.Admin]: 'admin.permissions.role.admin',
};

type Tab = 'modulos' | 'reservados';

export function Permissions() {
  const t = useT();
  const { user } = useAuth();
  const admin = isAdmin(user?.roles ?? []);
  const [tab, setTab] = useState<Tab>('modulos');

  if (!admin) {
    return (
      <section>
        <div className="page-head">
          <h2>{t('admin.permissions.title')}</h2>
        </div>
        <p className="muted">{t('admin.permissions.adminOnly')}</p>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('admin.permissions.title')}</h2>
          <p className="page-sub">{t('admin.permissions.subtitle')}</p>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'modulos' ? ' active' : ''}`}
          onClick={() => setTab('modulos')}
        >
          {t('admin.permissions.tabs.modules')}
        </button>
        <button
          type="button"
          className={`tab${tab === 'reservados' ? ' active' : ''}`}
          onClick={() => setTab('reservados')}
        >
          {t('admin.permissions.tabs.reserved')}
        </button>
      </div>

      {tab === 'modulos' ? <ModuleAccessPanel /> : <ReservedDataGrants />}
    </section>
  );
}

// ── Module access ──────────────────────────────────────────────────────────

function ModuleAccessPanel() {
  const t = useT();
  const [grants, setGrants] = useState<ModuleGrantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // New-grant form
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonSummaryDto[]>([]);
  const [selected, setSelected] = useState<PersonSummaryDto | null>(null);
  const [moduleKey, setModuleKey] = useState<PortalModule>(PortalModule.Colaboradores);
  const [canEdit, setCanEdit] = useState(false);

  // Table filters
  const [filterText, setFilterText] = useState('');
  const [filterModule, setFilterModule] = useState<PortalModule | ''>('');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setGrants(await api.listModuleGrants());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Debounced people search for the person picker.
  useEffect(() => {
    const term = query.trim();
    if (selected || term.length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    const handle = setTimeout(async () => {
      try {
        const people = await api.listPeople(term);
        if (active) setResults(people.slice(0, 8));
      } catch {
        if (active) setResults([]);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [query, selected]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.createModuleGrant({ personId: selected.id, module: moduleKey, canEdit });
      setSelected(null);
      setQuery('');
      setCanEdit(false);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setLevel(g: ModuleGrantDto, edit: boolean) {
    if (g.canEdit === edit) return;
    setError(null);
    try {
      await api.updateModuleGrant(g.id, edit);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await api.deleteModuleGrant(id);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const visible = useMemo(() => {
    const term = filterText.trim().toLowerCase();
    return grants.filter((g) => {
      if (filterModule && g.module !== filterModule) return false;
      if (!term) return true;
      return (
        g.personName.toLowerCase().includes(term) || (g.userName ?? '').toLowerCase().includes(term)
      );
    });
  }, [grants, filterText, filterModule]);

  return (
    <>
      <p className="muted">
        {t('admin.permissions.modulesPanel.introPart1')}
        <strong>{t('admin.permissions.modulesPanel.introView')}</strong>
        {t('admin.permissions.modulesPanel.introPart2')}
        <strong>{t('admin.permissions.modulesPanel.introEdit')}</strong>
        {t('admin.permissions.modulesPanel.introPart3')}
        <strong>{t('admin.permissions.modulesPanel.introUserName')}</strong>
        {t('admin.permissions.modulesPanel.introPart4')}
      </p>

      {error && <div className="alert error">{error}</div>}

      <form className="add-field-form" onSubmit={add} style={{ marginTop: 8 }}>
        <h3>{t('admin.permissions.modulesPanel.newTitle')}</h3>
        <div className="row">
          <label style={{ position: 'relative' }}>
            {t('admin.permissions.modulesPanel.collaborator')}
            {selected ? (
              <div className="actions-row" style={{ gap: 8, marginTop: 4 }}>
                <span className="badge approved">
                  {selected.fullName}
                  {selected.code ? ` · ${selected.code}` : ''}
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setSelected(null);
                    setQuery('');
                  }}
                >
                  {t('admin.permissions.change')}
                </button>
              </div>
            ) : (
              <>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('admin.permissions.modulesPanel.collaboratorPlaceholder')}
                  autoComplete="off"
                />
                {results.length > 0 && (
                  <ul className="picker-list">
                    {results.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="picker-item"
                          onClick={() => {
                            setSelected(p);
                            setResults([]);
                          }}
                        >
                          <span>{p.fullName}</span>
                          <span className="muted">
                            {[p.code, p.professionalEmail].filter(Boolean).join(' · ')}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </label>
          <label>
            {t('admin.permissions.modulesPanel.module')}
            <select
              value={moduleKey}
              onChange={(e) => setModuleKey(e.target.value as PortalModule)}
            >
              {ALL_MODULES.map((m) => (
                <option key={m} value={m}>
                  {t(`modules.${MODULE_KEY[m]}`)}
                </option>
              ))}
            </select>
            <span className="muted" style={{ fontSize: 12 }}>
              {t(`moduleHints.${MODULE_KEY[moduleKey]}`)}
            </span>
          </label>
          <label>
            {t('admin.permissions.modulesPanel.level')}
            <select
              value={canEdit ? 'edit' : 'view'}
              onChange={(e) => setCanEdit(e.target.value === 'edit')}
            >
              <option value="view">{t('admin.permissions.modulesPanel.levelView')}</option>
              <option value="edit">{t('admin.permissions.modulesPanel.levelEdit')}</option>
            </select>
          </label>
        </div>
        <div className="actions-row">
          <span />
          <button type="submit" disabled={busy || !selected}>
            {busy
              ? t('admin.permissions.modulesPanel.submitBusy')
              : t('admin.permissions.modulesPanel.submit')}
          </button>
        </div>
      </form>

      <div className="actions-row" style={{ marginTop: 24, marginBottom: 8, gap: 8 }}>
        <h3 style={{ margin: 0, flex: 1 }}>{t('admin.permissions.modulesPanel.grantsTitle')}</h3>
        <input
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={t('admin.permissions.modulesPanel.filterPlaceholder')}
          style={{ maxWidth: 220 }}
        />
        <select
          value={filterModule}
          onChange={(e) => setFilterModule(e.target.value as PortalModule | '')}
        >
          <option value="">{t('admin.permissions.modulesPanel.allModules')}</option>
          {ALL_MODULES.map((m) => (
            <option key={m} value={m}>
              {t(`modules.${MODULE_KEY[m]}`)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="muted">{t('common.loading')}</p>
      ) : visible.length === 0 ? (
        <p className="muted">
          {grants.length === 0
            ? t('admin.permissions.modulesPanel.emptyNone')
            : t('admin.permissions.modulesPanel.emptyFiltered')}
        </p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.permissions.modulesPanel.colCollaborator')}</th>
              <th>{t('admin.permissions.modulesPanel.colUserName')}</th>
              <th>{t('admin.permissions.modulesPanel.colModule')}</th>
              <th>{t('admin.permissions.modulesPanel.colLevel')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((g) => (
              <tr key={g.id}>
                <td>{g.personName}</td>
                <td>
                  {g.userName ?? (
                    <span className="muted">{t('admin.permissions.modulesPanel.noMatch')}</span>
                  )}
                </td>
                <td>{t(`modules.${MODULE_KEY[g.module]}`)}</td>
                <td>
                  <div className="seg">
                    <button
                      type="button"
                      className={`seg-btn${g.canEdit ? '' : ' on'}`}
                      onClick={() => void setLevel(g, false)}
                    >
                      {t('admin.permissions.modulesPanel.levelView')}
                    </button>
                    <button
                      type="button"
                      className={`seg-btn${g.canEdit ? ' on' : ''}`}
                      onClick={() => void setLevel(g, true)}
                    >
                      {t('admin.permissions.modulesPanel.levelEdit')}
                    </button>
                  </div>
                </td>
                <td>
                  <button type="button" className="link-button" onClick={() => void remove(g.id)}>
                    {t('admin.permissions.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// ── Reserved-data grants (existing) ──────────────────────────────────────────

function ReservedDataGrants() {
  const t = useT();
  const [grants, setGrants] = useState<PermissionGrantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [subjectType, setSubjectType] = useState<PermissionSubjectType>(PermissionSubjectType.Role);
  const [subjectId, setSubjectId] = useState<string>(Role.Hr);
  const [permission, setPermission] = useState<Permission>(Permission.ViewReserved);
  const [effect, setEffect] = useState<PermissionEffect>(PermissionEffect.Allow);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setGrants(await api.listGrants());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!subjectId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createGrant({ subjectType, subjectId: subjectId.trim(), permission, effect });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    await api.deleteGrant(id);
    await load();
  }

  return (
    <>
      <p className="muted">
        {t('admin.permissions.reserved.introPart1')}
        <strong>{t('admin.permissions.reserved.introDeny')}</strong>
        {t('admin.permissions.reserved.introPart2')}
      </p>

      {error && <div className="alert error">{error}</div>}

      <form className="add-field-form" onSubmit={add} style={{ marginTop: 8 }}>
        <h3>{t('admin.permissions.reserved.newTitle')}</h3>
        <div className="row">
          <label>
            {t('admin.permissions.reserved.subject')}
            <select
              value={subjectType}
              onChange={(e) => {
                const next = e.target.value as PermissionSubjectType;
                setSubjectType(next);
                setSubjectId(next === PermissionSubjectType.Role ? Role.Hr : '');
              }}
            >
              {Object.values(PermissionSubjectType).map((s) => (
                <option key={s} value={s}>
                  {t(SUBJECT_LABEL_KEYS[s])}
                </option>
              ))}
            </select>
          </label>
          <label>
            {subjectType === PermissionSubjectType.Role
              ? t('admin.permissions.reserved.subjectRole')
              : subjectType === PermissionSubjectType.User
                ? t('admin.permissions.reserved.subjectUser')
                : t('admin.permissions.reserved.subjectGroup')}
            {subjectType === PermissionSubjectType.Role ? (
              <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
                {Object.values(Role).map((r) => (
                  <option key={r} value={r}>
                    {t(ROLE_LABEL_KEYS[r])}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                placeholder={
                  subjectType === PermissionSubjectType.User
                    ? t('admin.permissions.reserved.subjectUserPlaceholder')
                    : t('admin.permissions.reserved.subjectGroupPlaceholder')
                }
              />
            )}
          </label>
        </div>
        <div className="row">
          <label>
            {t('admin.permissions.reserved.permission')}
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value as Permission)}
            >
              {Object.values(Permission).map((p) => (
                <option key={p} value={p}>
                  {t(PERMISSION_LABEL_KEYS[p])}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('admin.permissions.reserved.effect')}
            <select value={effect} onChange={(e) => setEffect(e.target.value as PermissionEffect)}>
              <option value={PermissionEffect.Allow}>
                {t('admin.permissions.reserved.effectAllow')}
              </option>
              <option value={PermissionEffect.Deny}>
                {t('admin.permissions.reserved.effectDeny')}
              </option>
            </select>
          </label>
        </div>
        <div className="actions-row">
          <span />
          <button type="submit" disabled={busy}>
            {busy
              ? t('admin.permissions.reserved.submitBusy')
              : t('admin.permissions.reserved.submit')}
          </button>
        </div>
      </form>

      <h3 style={{ marginTop: 24 }}>{t('admin.permissions.reserved.rulesTitle')}</h3>
      {loading ? (
        <p className="muted">{t('common.loading')}</p>
      ) : grants.length === 0 ? (
        <p className="muted">{t('admin.permissions.reserved.empty')}</p>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.permissions.reserved.colSubject')}</th>
              <th>{t('admin.permissions.reserved.colIdentifier')}</th>
              <th>{t('admin.permissions.reserved.colPermission')}</th>
              <th>{t('admin.permissions.reserved.colEffect')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => (
              <tr key={g.id}>
                <td>{t(SUBJECT_LABEL_KEYS[g.subjectType])}</td>
                <td>
                  {g.subjectType === PermissionSubjectType.Role
                    ? ROLE_LABEL_KEYS[g.subjectId as Role]
                      ? t(ROLE_LABEL_KEYS[g.subjectId as Role])
                      : g.subjectId
                    : g.subjectId}
                </td>
                <td>{t(PERMISSION_LABEL_KEYS[g.permission])}</td>
                <td>
                  <span
                    className={`badge ${g.effect === PermissionEffect.Allow ? 'approved' : 'rejected'}`}
                  >
                    {g.effect === PermissionEffect.Allow
                      ? t('admin.permissions.reserved.effectAllow')
                      : t('admin.permissions.reserved.effectDeny')}
                  </span>
                </td>
                <td>
                  <button type="button" className="link-button" onClick={() => void remove(g.id)}>
                    {t('admin.permissions.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
